<template>
  <v-dialog
    :value="value"
    max-width="700px"
    @click:outside="close"
  >
    <v-card>
      <v-card-title class="pa-0">
        <v-toolbar color="grey lighten-1">
          <v-btn
            icon
            class="mr-2"
            @click="close"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <div class="text-capitalize">
            {{ `${verbiage[0] || ''} ${verbiage[1] || ''}...` }}
          </div>
          <v-spacer />
          <key-refresher
            ref="lockTimerRefresher"
            unique-key="lock-timer"
          >
            <template v-slot="{ refreshKey }">
              <lock-timer
                :key="refreshKey"
                v-bind="getLockTimerSettings(servers, {children: true, recordType: apiRecordCommand}, close)"
              />
            </template>
          </key-refresher>
        </v-toolbar>
      </v-card-title>
      <api-errors :errors="errors" />
      <loading-bar v-if="isLoading" />
      <div v-else>
        <v-card-text>
          <div>Please select whether to:</div>
          <div class="pl-5">
            <span class="font-weight-bold text-lowercase">{{ verbiage[0] || '' }}</span>
            the selected server(s) {{ verbiage[1] || '' }} to this
            <span class="font-weight-bold">table</span>
            for this Meal Period
          </div>
          <div class="pl-5">
            Or,
            <span class="font-weight-bold text-lowercase">{{ verbiage[0] || '' }}</span>
            the selected server(s) to this
            <span class="font-weight-bold">reservation</span> only.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn
            text
            @click="close"
          >
            Cancel
          </v-btn>
          <v-spacer />
          <v-tooltip
            top
            color="warning"
          >
            <template #activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="allowByTable ? {} : on"
              >
                <v-btn
                  :disabled="disabled || !!isLoading || !reservationId || !allowByTable"
                  text
                  :color="actionColor"
                  @click="save(servers, 'TABLE', requestType)"
                >
                  {{ `${verbiage[0]} ${verbiage[1]} Table` }}
                </v-btn>
              </div>
            </template>
            Table Assignment Required
          </v-tooltip>
          <v-btn
            :disabled="disabled || !!isLoading || !reservationId"
            text
            :color="actionColor"
            @click="save(servers, 'RESERVATION', requestType)"
          >
            {{ `${verbiage[0]} ${verbiage[1]} Reservation` }}
          </v-btn>
        </v-card-actions>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import recordLockHelper from '@/ft-vue-core/mixins/recordLockHelper'
import LoadingBar from '../helperComponents/LoadingBar'
export default {
  name: 'ConfirmServerListChangeDialog',
  components: {LoadingBar},
  mixins: [recordLockHelper],
  props: {
    value: Boolean,
    /** Definition of whether this dialog will read ["Remove", "from"] or ["Add", "to"] */
    verbiage: {
      type: Array,
      validator (value) {return value?.length === 2},
      required: true
    },
    /** ID(s) of servers selected in previous step */
    serverIds: {
      type: Array,
      default () {return []}
    },
    /** Date selected in Floor Plan */
    date: {
      type: String,
      required: true
    },
    /** ID of selected Reservation */
    reservationId: Number,
    allowByTable: Boolean,
  },
  data () {
    return {
      /** Location for servers queried from the API */
      servers: [],
      disabled: false,
      apiRecordCommand: 'diningServer',
    }
  },
  computed: {
    /** Controls color of buttons on dialog by checking for destructive action verbiage */
    actionColor () {
      return this.verbiage[0].toLowerCase() === 'add' ? 'primary' : 'error'
    },
    /**
     * Indicates to `save` method white request type to send to API
     * @see save
     */
    requestType () {
      return this.actionColor === 'primary' ? 'put' : 'delete'
    }
  },
  watch: {
    /**
     * IF `true`: Lock a copy of each selected server in `serverIds`
     *
     * ELSE: Release our locks and trigger key-refresh of dialog
     * @param {Boolean} newVal Controler for opening/closing this dialog
     */
    value (newVal) {
      if (!newVal) {
        if (!this.servers?.length) this.$emit('close')
        else this.releaseLock(this.servers, () => this.$emit('close'))
      } else {
        const command = this.withResourceLockHandler({
          command: this.apiRecordCommand,
          load: this.servers,
          loading: this.setIsLoading,
          error: (e) => {
            this.errors.push(e.message)
            this.disabled = true
          },
          params: {
            children: true,
            filter: [{field: 'id', operator: 'IN', values: this.serverIds}]
          }
        })
        this.$ftAPI().get(this.withResourceLockHandler(command))
      }
    }
  },
  methods: {
    /** Used to reduce characters typed above */
    close () {this.$emit('input', false)},
    /**
     * Execute a request to mangage the whitelist/blacklist of selected servers
     * @param {Array} servers Array of servers who have been selected for update
     * @param {String} action Action corresponding with ManageServerListAction enum
     * @param {String} requestType 'put' or 'delete' to specify type of request sent
     */
    save (servers, action, requestType = 'put') {
      if (!['put', 'delete'].includes(requestType)) return
      const {date, reservationId} = this.$props
      this.$ftAPI()[requestType]?.(this.addResourceErrorHandler({
        command: 'diningManageServerLists',
        loading: this.setIsLoading,
        afterLoad: () => {
          this.close()
          this.$emit('saved')
        }
      }), {servers, action, date, reservationId})
    }
  }
}
</script>

<style scoped>
.v-tooltip__content {
  opacity: 1;
}
</style>
